import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ChatDockContext = createContext(null);
const STORAGE_KEY = "so_chat_dock";
const MAX_OPEN = 3;

function loadStored() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return { chats: [], minimized: [] };
    const p = JSON.parse(raw);
    return { chats: Array.isArray(p.chats) ? p.chats : [], minimized: Array.isArray(p.minimized) ? p.minimized : [] };
  } catch (e) { return { chats: [], minimized: [] }; }
}

/** Holds the LinkedIn-style docked chat windows so they persist across page
 *  navigation (and a full refresh, via sessionStorage). */
export function ChatDockProvider({ children }) {
  const { user } = useAuth();
  // Lazy init reads sessionStorage synchronously on first render, so the restore
  // can't be clobbered by the persist effect (avoids a read-after-write race).
  const [chats, setChats] = useState(() => loadStored().chats); // [{ id, other }]
  const [minimized, setMinimized] = useState(() => loadStored().minimized); // [id]

  // Persist whenever the dock changes.
  useEffect(() => {
    try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ chats, minimized })); } catch (e) { /* ignore */ }
  }, [chats, minimized]);

  // Clear only on a real logout (logged-in -> logged-out), not during the initial
  // auth load where `user` is briefly null (which would wipe a rehydrated dock on refresh).
  const wasLoggedIn = useRef(false);
  useEffect(() => {
    if (wasLoggedIn.current && !user) {
      setChats([]); setMinimized([]);
      try { sessionStorage.removeItem(STORAGE_KEY); } catch (e) { /* ignore */ }
    }
    if (user) wasLoggedIn.current = true;
  }, [user]);

  const openChat = useCallback((id, other) => {
    setChats((prev) => {
      const exists = prev.find((c) => c.id === id);
      let next = exists
        ? prev.map((c) => (c.id === id ? { ...c, other: other || c.other } : c))
        : [...prev, { id, other }];
      if (next.length > MAX_OPEN) next = next.slice(next.length - MAX_OPEN);
      return next;
    });
    setMinimized((prev) => prev.filter((x) => x !== id));
  }, []);

  const openChatWithUser = useCallback(async (targetUserId) => {
    const { data } = await api.post("/messages/conversations", { target_user_id: targetUserId });
    openChat(data.id, data.other);
    return data;
  }, [openChat]);

  const closeChat = useCallback((id) => {
    setChats((prev) => prev.filter((c) => c.id !== id));
    setMinimized((prev) => prev.filter((x) => x !== id));
  }, []);

  const toggleMinimize = useCallback((id) => {
    setMinimized((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }, []);

  const value = { chats, minimized, openChat, openChatWithUser, closeChat, toggleMinimize };
  return <ChatDockContext.Provider value={value}>{children}</ChatDockContext.Provider>;
}

export const useChatDock = () => useContext(ChatDockContext) || {
  chats: [], minimized: [],
  openChat: () => {}, openChatWithUser: async () => {}, closeChat: () => {}, toggleMinimize: () => {},
};
